export type ClarityOneInvoiceKind = "portal" | "deal";
export type ClarityOneInvoicePaymentGroup = "outstanding" | "paid";
export type ClarityOneInvoiceDisplayStatus = "Outstanding" | "Partially paid" | "Paid";

export type ClarityOneInvoiceCustomerView = {
  paymentGroup: ClarityOneInvoicePaymentGroup;
  displayStatus: ClarityOneInvoiceDisplayStatus;
};

function normalizeInvoiceStatus(status: string | null | undefined): string {
  return String(status ?? "")
    .trim()
    .toLowerCase()
    .replace(/[\s-]+/g, "_");
}

/**
 * Customer-safe invoice list classifier.
 * Unknown, draft, cancelled, voided, and unsupported statuses fail closed (null).
 * Never treat an unknown status as Paid.
 */
export function classifyClarityOneInvoiceForCustomer(
  kind: ClarityOneInvoiceKind,
  rawStatus: string | null | undefined,
): ClarityOneInvoiceCustomerView | null {
  const status = normalizeInvoiceStatus(rawStatus);
  if (!status) return null;
  if (status === "voided" || status === "void" || status === "cancelled" || status === "canceled") {
    return null;
  }
  if (status === "draft" || status === "pending") return null;

  if (status === "paid") {
    return { paymentGroup: "paid", displayStatus: "Paid" };
  }

  if (kind === "portal") {
    if (status === "raised") {
      return { paymentGroup: "outstanding", displayStatus: "Outstanding" };
    }
    if (status === "partial") {
      return { paymentGroup: "outstanding", displayStatus: "Partially paid" };
    }
    return null;
  }

  if (kind === "deal") {
    if (status === "raised" || status === "outstanding") {
      return { paymentGroup: "outstanding", displayStatus: "Outstanding" };
    }
    return null;
  }

  return null;
}

export function partitionClarityOneInvoices<T extends { paymentGroup: ClarityOneInvoicePaymentGroup }>(
  invoices: readonly T[],
): { outstanding: T[]; paid: T[] } {
  const outstanding: T[] = [];
  const paid: T[] = [];
  for (const invoice of invoices) {
    if (invoice.paymentGroup === "outstanding") outstanding.push(invoice);
    else if (invoice.paymentGroup === "paid") paid.push(invoice);
  }
  return { outstanding, paid };
}

export function invoiceHref(
  kind: ClarityOneInvoiceKind,
  token: string | null | undefined,
): string | null {
  if (!token) return null;
  return kind === "portal" ? `/invoices/portal/${token}` : `/invoices/deal/${token}`;
}
